
interface SubmissionHeaderProps {
  activeStep: number;
  totalSteps?: number;
  completedSteps?: Set<number>;
  onBack?: () => void;
  onGoToStep?: (step: number) => void;
}

const stepLabels = [
  "Basic Info",
  "Company Info",
  "Upload Documents",
  "Application",
];

function SubmissionHeader({
  activeStep,
  totalSteps = 4,
  completedSteps = new Set(),
  onBack,
  onGoToStep,
}: SubmissionHeaderProps) {
  const isStepClickable = (step: number) =>
    completedSteps.has(step) && step < activeStep && !!onGoToStep;

  const handleStepClick = (step: number) => {
    if (isStepClickable(step)) {
      onGoToStep!(step);
    }
  };

  const handleStepKeyDown = (
    e: React.KeyboardEvent,
    step: number
  ) => {
    if ((e.key === "Enter" || e.key === " ") && isStepClickable(step)) {
      e.preventDefault();
      onGoToStep!(step);
    }
  };

  return (
    <div className="sticky top-0 z-40 bg-white border-b border-gray-200">
      <div className="flex items-center gap-0 px-4 md:px-8">
        {/* Back button — left side with right border divider */}
        {onBack && activeStep > 1 ? (
          <button
            type="button"
            onClick={onBack}
            className="flex items-center gap-1.5 text-sm font-medium text-gray-500 hover:text-gray-900 transition-colors duration-150 pr-4 md:pr-6 border-r border-gray-200 shrink-0 py-3 md:py-4"
            aria-label="Go to previous step"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 12H5M12 5l-7 7 7 7"
              />
            </svg>
            Back
          </button>
        ) : (
          /* Empty spacer to keep stepper aligned when no back button */
          <div className="w-0" />
        )}

        {/* Desktop Stepper — fills remaining space */}
        <div className="hidden md:flex items-center flex-1 py-3 pl-6">
          {Array.from({ length: totalSteps }, (_, i) => i + 1).map(
            (step, index) => {
              const isCompleted = step < activeStep;
              const isActive = step === activeStep;
              const clickable = isStepClickable(step);

              return (
                <div key={step} className="contents">
                  {/* Step: circle + label */}
                  <div
                    className={`
                      flex items-center gap-2 shrink-0
                      ${clickable
                        ? "cursor-pointer rounded-md px-1.5 py-1 -mx-1.5 -my-1 transition-colors duration-150 hover:bg-blue-50"
                        : ""
                      }
                    `}
                    role={clickable ? "button" : undefined}
                    tabIndex={clickable ? 0 : undefined}
                    title={
                      clickable
                        ? `Go back to ${stepLabels[index]}`
                        : undefined
                    }
                    onClick={() => handleStepClick(step)}
                    onKeyDown={(e) => handleStepKeyDown(e, step)}
                  >
                    {/* Circle */}
                    <div
                      className={`
                        w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold shrink-0 transition-all duration-200
                        ${isCompleted
                          ? clickable
                            ? "bg-green-700 text-white group-hover:bg-blue-700"
                            : "bg-green-700 text-white"
                          : isActive
                            ? "bg-amber-700 text-white"
                            : "bg-gray-200 text-gray-500"
                        }
                      `}
                    >
                      {isCompleted ? (
                        <svg
                          className="w-3.5 h-3.5"
                          fill="none"
                          stroke="white"
                          strokeWidth="2.5"
                          viewBox="0 0 24 24"
                        >
                          <path d="M20 6L9 17l-5-5" />
                        </svg>
                      ) : (
                        step
                      )}
                    </div>

                    {/* Label */}
                    <span
                      className={`
                        text-[13px] font-medium whitespace-nowrap transition-colors duration-200
                        ${isCompleted
                          ? "text-green-700"
                          : isActive
                            ? "text-amber-700"
                            : "text-gray-400"
                        }
                      `}
                    >
                      {stepLabels[index]}
                    </span>
                  </div>

                  {/* Connector line between steps */}
                  {index < totalSteps - 1 && (
                    <div
                      className={`
                        flex-1 h-0.5 mx-2 min-w-[32px] transition-colors duration-300
                        ${isCompleted ? "bg-green-700" : "bg-gray-200"}
                      `}
                    />
                  )}
                </div>
              );
            }
          )}
        </div>

        {/* Mobile current step indicator */}
        <div className="md:hidden flex-1 py-3 flex items-center justify-end pr-2 sm:pr-4">
          <span className="text-sm font-semibold text-gray-900">{stepLabels[activeStep - 1]}</span>
        </div>
      </div>

      {/* Mobile Progress Bar */}
      <div className="md:hidden h-1.5 w-full bg-gray-100">
        <div
          className="h-full bg-amber-500 transition-all duration-300 rounded-r-full"
          style={{ width: `${(activeStep / totalSteps) * 100}%` }}
        />
      </div>
    </div>
  );
}

export default SubmissionHeader;
